import {
  ArrowBack,
  Business,
  Category,
  Search as SearchIcon,
  Description,
  Event,
} from "@material-ui/icons";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import moment from "moment";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import styled, { keyframes, StyleSheetManager } from "styled-components";
import NewWindow from "react-new-window";
import "react-tabs/style/react-tabs.css";

import fetchClient from "../../api/fetchClient";
import Loader from "../../components/Loader";
import LogEvents from "../../components/LogEvents";
import SearchLogs from "../../components/SearchLogs";
import message from "../../helper/message";

const LOG_TYPE_TEMPLATES = [
  { value: "ifs-material",      description: "Material (Item)" },
  { value: "ifs-stock",         description: "Estoque" },
  { value: "ifs-stocktransfer", description: "Transferência de Estoque" },
];

function IFSLogs() {
  const history = useHistory();

  const [aplicacoes, setAplicacoes]   = useState([]);
  const [aplicacaoId, setAplicacaoId] = useState("");
  const [type, setType]               = useState("");
  const [groupsLogs, setGroupsLogs]   = useState([]);
  const [isLoading, setIsLoading]     = useState(false);
  const [groupLog, setGroupLog]       = useState(null);
  const [isOpened, setIsOpened]       = useState(false);
  const [logWindow, setLogWindow]     = useState(null);
  const [windowFeatures, setWindowFeatures] = useState(
    "left=0,top=0,width=1200,height=700,resizable=yes,scrollbars=yes"
  );
  const [nextPage, setNextPage]       = useState({ has: false, link: null });

  // carrega aplicações e auto-seleciona a primeira
  useEffect(() => {
    fetchClient.get("/ifs/getAllAplicacao").then(({ data }) => {
      const list = (Array.isArray(data) ? data : data.items || []).filter(a => a.id && !a.id.includes("#"));
      setAplicacoes(list);
      if (list.length > 0) setAplicacaoId(list[0].id);
    }).catch(() => {});
  }, []);

  // ajusta tamanho da janela de log
  useEffect(() => {
    if (typeof window === "undefined") return;
    const w = window.screen?.availWidth  || window.innerWidth  || 1600;
    const h = window.screen?.availHeight || window.innerHeight || 900;
    setWindowFeatures(`left=0,top=0,width=${w},height=${h},resizable=yes,scrollbars=yes`);
  }, []);

  // busca streams quando empresa + tipo mudam
  useEffect(() => {
    if (!aplicacaoId || !type) return;
    getLogs(aplicacaoId, type);
    // eslint-disable-next-line
  }, [aplicacaoId, type]);

  // limpa streams ao trocar empresa
  useEffect(() => {
    setType("");
    setGroupsLogs([]);
    setNextPage({ has: false, link: null });
  }, [aplicacaoId]);

  const getLogs = async (appId, logType, token = null) => {
    try {
      if (!appId || !logType) return;
      setIsLoading(true);
      const params = new URLSearchParams({ type: logType });
      if (token) params.set("nextToken", token);
      const { data } = await fetchClient.get(`/ifs/getAllLogs/${appId}?${params}`);
      setGroupsLogs(old => token ? [...old, ...(data.logStreams || [])] : (data.logStreams || []));
      setNextPage(data.nextToken ? { has: true, link: data.nextToken } : { has: false, link: null });
    } catch (err) {
      message.error(err.response?.data?.message || "Erro ao carregar logs");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (search) => {
    if (!aplicacaoId) return;
    if (!search || !search.trim()) {
      getLogs(aplicacaoId, type);
      return;
    }
    setIsLoading(true);
    try {
      const { data } = await fetchClient.post("/ifs/queryLogs", {
        queryString: search,
        type,
        aplicacaoId,
      });
      setGroupsLogs(data);
    } catch {
      message.error("Erro ao buscar logs");
    } finally {
      setIsLoading(false);
    }
  };

  const closeLogWindow = () => { setIsOpened(false); setLogWindow(null); };

  const selectedAplicacao = aplicacoes.find(a => a.id === aplicacaoId);
  const selectedTypeLabel = LOG_TYPE_TEMPLATES.find(o => o.value === type)?.description;

  return (
    <Container>
      {isOpened && (
        <NewWindow
          title="Logs IFS"
          features={windowFeatures}
          onOpen={win => setLogWindow(win)}
          onUnload={closeLogWindow}
          onClose={closeLogWindow}
        >
          {logWindow && (
            <StyleSheetManager target={logWindow.document.head}>
              <LogEvents
                groupLog={groupLog}
                empresa={aplicacaoId}
                ifs={true}
                type={type}
              />
            </StyleSheetManager>
          )}
        </NewWindow>
      )}

      <Page>
        <PageContent>
          <ModernHeader>
            <HeaderLeft>
              <BackButton onClick={() => history.goBack()}>
                <ArrowBack />
              </BackButton>
              <HeaderContent>
                <PageTitle>
                  <Description style={{ marginRight: "12px", fontSize: "32px" }} />
                  Logs IFS
                </PageTitle>
                <PageSubtitle>Visualize e busque logs de eventos da integração IFS</PageSubtitle>
              </HeaderContent>
            </HeaderLeft>
            <SelectedChips>
              {selectedAplicacao && (
                <SelectedBadge>
                  <Business style={{ fontSize: "16px", marginRight: "4px" }} />
                  {selectedAplicacao.name}
                </SelectedBadge>
              )}
              {selectedTypeLabel && (
                <SelectedBadge>
                  <Category style={{ fontSize: "16px", marginRight: "4px" }} />
                  {selectedTypeLabel}
                </SelectedBadge>
              )}
            </SelectedChips>
          </ModernHeader>

          <FiltersCard>
            <SectionHeader>
              <SectionIcon>
                <SearchIcon />
              </SectionIcon>
              <SectionTitle>Filtros de Busca</SectionTitle>
            </SectionHeader>
            <FormGrid>
              <StyledFieldContainer>
                <FieldLabel>
                  <Business style={{ fontSize: "18px", marginRight: "8px", color: "#ff6b35" }} />
                  Empresa
                </FieldLabel>
                <TextField
                  select fullWidth variant="outlined"
                  value={aplicacaoId}
                  onChange={e => setAplicacaoId(e.target.value)}
                >
                  <MenuItem value="">Selecionar empresa</MenuItem>
                  {aplicacoes.map(a => (
                    <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                  ))}
                </TextField>
              </StyledFieldContainer>

              <StyledFieldContainer>
                <FieldLabel>
                  <Category style={{ fontSize: "18px", marginRight: "8px", color: "#ff6b35" }} />
                  Tipo de Log
                </FieldLabel>
                <TextField
                  select fullWidth variant="outlined"
                  value={type}
                  onChange={e => setType(e.target.value)}
                  disabled={!aplicacaoId}
                >
                  <MenuItem value="">Selecionar tipo</MenuItem>
                  {LOG_TYPE_TEMPLATES.map(o => (
                    <MenuItem key={o.value} value={o.value}>{o.description}</MenuItem>
                  ))}
                </TextField>
              </StyledFieldContainer>

              <StyledFieldContainer fullWidth>
                <FieldLabel>
                  <SearchIcon style={{ fontSize: "18px", marginRight: "8px", color: "#ff6b35" }} />
                  Busca Avançada (pesquisa no conteúdo dos logs)
                </FieldLabel>
                <SearchWrapper>
                  <SearchLogs
                    onSearch={handleSearch}
                    tenent={aplicacaoId}
                    type={type}
                    setLoading={setIsLoading}
                    setGroupsLogs={setGroupsLogs}
                  />
                </SearchWrapper>
              </StyledFieldContainer>
            </FormGrid>

            <FilterHelper>
              <Event style={{ fontSize: "16px", marginRight: "8px" }} />
              Use a Busca Avançada para pesquisar dentro do conteúdo dos logs. Deixe em branco e pressione buscar para trazer todos os logs.
            </FilterHelper>
          </FiltersCard>

          {isLoading ? (
            <LoaderWrapper><Loader /></LoaderWrapper>
          ) : !aplicacaoId || !type ? (
            <ResultsCard>
              <EmptyState>
                <Description style={{ fontSize: "48px", color: "#cbd5e1", marginBottom: "16px" }} />
                <EmptyTitle>Nenhum filtro selecionado</EmptyTitle>
                <EmptyText>Escolha uma empresa e um tipo de log para visualizar os eventos disponíveis.</EmptyText>
              </EmptyState>
            </ResultsCard>
          ) : (
            <ResultsCard>
              <SectionHeader>
                <SectionInfo>
                  <SectionTitle>Streams recentes</SectionTitle>
                  <SectionSubtitle>
                    {groupsLogs.length > 0
                      ? `Mostrando ${groupsLogs.length} ${groupsLogs.length === 1 ? "stream" : "streams"}`
                      : "Nenhum stream encontrado"}
                  </SectionSubtitle>
                </SectionInfo>
                {nextPage.has && <Tag>Mais resultados disponíveis</Tag>}
              </SectionHeader>

              {groupsLogs.length === 0 ? (
                <EmptyState>
                  Nenhum registro encontrado para essa combinação. Ajuste os filtros ou tente novamente mais tarde.
                </EmptyState>
              ) : (
                <>
                  <TableWrapper>
                    <StreamsTable>
                      <thead>
                        <tr>
                          <TableHead>ID do stream</TableHead>
                          <TableHead>Último evento</TableHead>
                          <TableHead align="right">Ações</TableHead>
                        </tr>
                      </thead>
                      <tbody>
                        {groupsLogs.map(({ logStreamName, lastEventTimestamp }) => (
                          <TableRow key={logStreamName}>
                            <StreamName title={logStreamName}>{logStreamName}</StreamName>
                            <EventTime>
                              {moment(lastEventTimestamp).format("DD/MM/YYYY HH:mm")}
                            </EventTime>
                            <TableActions>
                              <ConsultResult onClick={() => { setGroupLog(logStreamName); setIsOpened(true); }}>
                                Consultar
                              </ConsultResult>
                            </TableActions>
                          </TableRow>
                        ))}
                      </tbody>
                    </StreamsTable>
                  </TableWrapper>

                  {nextPage.has && (
                    <MoreWrapper>
                      <MoreButton onClick={() => getLogs(aplicacaoId, type, nextPage.link)}>
                        Carregar mais
                      </MoreButton>
                    </MoreWrapper>
                  )}
                </>
              )}
            </ResultsCard>
          )}
        </PageContent>
      </Page>
    </Container>
  );
}

export default IFSLogs;

// ─── STYLES ───────────────────────────────────────────────────────────────────
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  width: 100%; min-height: calc(100vh - 65px);
  padding: 24px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  font-family: 'Mulish', sans-serif;
  box-sizing: border-box;
`;
const Page        = styled.div`max-width:1400px;margin:0 auto;width:100%;box-sizing:border-box;`;
const PageContent = styled.div`display:flex;flex-direction:column;gap:24px;width:100%;box-sizing:border-box;`;

const ModernHeader = styled.div`
  padding:24px 0;margin-bottom:8px;
  display:flex;justify-content:space-between;align-items:center;gap:20px;
  animation:${fadeIn} .6s ease;width:100%;box-sizing:border-box;
`;
const HeaderLeft    = styled.div`display:flex;align-items:center;gap:16px;flex:1;`;
const HeaderContent = styled.div`flex:1;`;
const PageTitle     = styled.h1`font-size:28px;font-weight:700;color:#2c3e50;margin:0 0 6px;display:flex;align-items:center;font-family:'Mulish',sans-serif;`;
const PageSubtitle  = styled.p`font-size:14px;color:#7f8c8d;margin:0;font-family:'Mulish',sans-serif;`;
const BackButton    = styled.button`
  background:rgba(255,107,53,.1);border:none;border-radius:12px;
  width:42px;height:42px;display:flex;align-items:center;justify-content:center;
  cursor:pointer;color:#ff6b35;transition:all .3s;flex-shrink:0;
  &:hover{background:rgba(255,107,53,.2);transform:translateX(-4px);}
`;
const SelectedChips = styled.div`display:flex;flex-wrap:wrap;gap:10px;`;
const SelectedBadge = styled.div`
  display:inline-flex;align-items:center;padding:8px 16px;
  background:rgba(255,107,53,.1);color:#ff6b35;border-radius:20px;
  font-size:13px;font-weight:600;border:1px solid rgba(255,107,53,.2);
  font-family:'Mulish',sans-serif;
`;

const FiltersCard = styled.div`
  background:white;border:1px solid #e8e8e8;border-radius:16px;padding:24px;
  box-shadow:0 2px 8px rgba(0,0,0,.06);animation:${fadeIn} .5s ease .1s both;
  width:100%;box-sizing:border-box;
`;
const SectionHeader = styled.div`
  display:flex;align-items:center;margin-bottom:20px;padding-bottom:16px;
  border-bottom:2px solid #f0f0f0;width:100%;box-sizing:border-box;
`;
const SectionIcon  = styled.div`
  width:40px;height:40px;border-radius:12px;display:flex;align-items:center;justify-content:center;
  background:rgba(255,107,53,.1);color:#ff6b35;margin-right:12px;
  svg{font-size:22px;}
`;
const SectionTitle    = styled.h3`font-size:18px;font-weight:700;color:#2c3e50;margin:0;font-family:'Mulish',sans-serif;`;
const SectionInfo     = styled.div`display:flex;flex-direction:column;gap:4px;`;
const SectionSubtitle = styled.span`font-size:14px;color:#64748b;`;
const Tag = styled.span`
  background:#e3f7ff;color:#05668d;padding:6px 12px;border-radius:999px;
  font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:.05em;
`;

const FormGrid = styled.div`
  display:grid;grid-template-columns:repeat(2,1fr);gap:20px;
  @media(max-width:600px){grid-template-columns:1fr;}
`;

const StyledFieldContainer = styled.div`
  display:flex;flex-direction:column;
  grid-column: ${p => p.fullWidth ? "1 / -1" : "auto"};
  .MuiOutlinedInput-root{border-radius:10px;&.Mui-focused{box-shadow:0 4px 12px rgba(255,107,53,.2);}}
  .MuiOutlinedInput-input,.MuiInputLabel-outlined{font-family:'Mulish',sans-serif;}
`;
const SearchWrapper = styled.div`width:100%;>div{width:100%;}`;
const FieldLabel = styled.label`font-size:14px;font-weight:600;color:#2c3e50;margin-bottom:8px;display:flex;align-items:center;font-family:'Mulish',sans-serif;`;
const FilterHelper = styled.div`
  margin-top:20px;padding:14px 18px;
  background:rgba(255,107,53,.05);border-left:4px solid #ff6b35;border-radius:8px;
  font-size:13px;color:#5c6b7a;font-family:'Mulish',sans-serif;display:flex;align-items:center;line-height:1.5;
`;

const ResultsCard = styled.div`
  width:100%;background:white;border:1px solid #e8e8e8;border-radius:16px;padding:28px;
  box-shadow:0 2px 8px rgba(0,0,0,.06);animation:${fadeIn} .5s ease;box-sizing:border-box;
`;
const LoaderWrapper = styled.div`
  display:flex;align-items:center;justify-content:center;min-height:320px;
  background:white;border-radius:16px;box-shadow:0 2px 8px rgba(0,0,0,.06);width:100%;
`;

const TableWrapper  = styled.div`width:100%;overflow-x:auto;margin-top:12px;`;
const StreamsTable  = styled.table`width:100%;border-collapse:collapse;min-width:520px;`;
const TableHead     = styled.th`text-align:${p=>p.align||"left"};font-size:13px;letter-spacing:.05em;text-transform:uppercase;color:#6b7280;padding:12px 16px;border-bottom:1px solid #e5e7eb;`;
const TableRow      = styled.tr`border-bottom:1px solid #eef2f7;&:hover{background:#f9fbff;}`;
const StreamName    = styled.td`font-weight:600;color:#0f172a;padding:12px 16px;max-width:360px;overflow:hidden;text-overflow:ellipsis;`;
const EventTime     = styled.td`padding:12px 16px;color:#4c5968;font-size:14px;`;
const TableActions  = styled.td`padding:12px 16px;text-align:right;`;

const ConsultResult = styled.button`
  background:linear-gradient(135deg,#ff6b35 0%,#e55a28 100%);color:#fff;
  border:none;border-radius:10px;padding:8px 18px;font-size:13px;font-weight:600;
  cursor:pointer;font-family:'Mulish',sans-serif;transition:all .3s;
  box-shadow:0 2px 8px rgba(255,107,53,.3);
  &:hover{transform:translateY(-2px);box-shadow:0 4px 12px rgba(255,107,53,.4);}
`;
const MoreWrapper = styled.div`display:flex;justify-content:center;margin-top:16px;`;
const MoreButton  = styled.button`
  min-width:180px;height:44px;border:none;
  background:linear-gradient(135deg,#ff6b35 0%,#e55a28 100%);
  color:#fff;font-size:15px;font-weight:600;border-radius:12px;padding:0 32px;
  cursor:pointer;font-family:'Mulish',sans-serif;transition:all .3s;
  box-shadow:0 4px 12px rgba(255,107,53,.3);
  &:hover{transform:translateY(-2px);box-shadow:0 6px 16px rgba(255,107,53,.4);}
`;

const EmptyState = styled.div`width:100%;padding:64px 48px;display:flex;flex-direction:column;align-items:center;text-align:center;font-family:'Mulish',sans-serif;`;
const EmptyTitle = styled.h3`font-size:20px;font-weight:700;color:#2c3e50;margin:0 0 8px;font-family:'Mulish',sans-serif;`;
const EmptyText  = styled.p`font-size:14px;color:#7f8c8d;margin:0;max-width:400px;line-height:1.5;font-family:'Mulish',sans-serif;`;
